import { useEffect, useState, type FormEvent } from "react";
import { ApiError, api, type User } from "./api";
import { errorKey, useT } from "./i18n";
import * as passkeys from "./passkeys";

export default function Login({
  onLogin,
  onPasskey,
}: {
  onLogin: (email: string, password: string) => Promise<void>;
  onPasskey: (u: User) => void;
}) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"signIn" | "forgot">("signIn");
  const [notice, setNotice] = useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = useState(false);

  useEffect(() => {
    void passkeys.isAvailable().then(setHasPasskey);
  }, []);

  async function withPasskey() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      onPasskey(await passkeys.login());
    } catch (err) {
      setError(t(err instanceof ApiError ? errorKey(err.code) : "error.passkey_login_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "forgot") {
        await api.forgotPassword(email);
        // The backend answers the same whether or not the account exists, so as
        // not to give away which emails are registered. The wording is ours
        // rather than the server's, so it follows the reader's language.
        setNotice(t("reset.linkSent"));
      } else {
        await onLogin(email, password);
      }
    } catch (err) {
      setError(t(err instanceof ApiError ? errorKey(err.code) : "error.session_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-5">
      <h1 className="flex items-center gap-3 text-4xl font-semibold tracking-tight">
        <img src="/icon.svg" alt="" width="44" height="44" className="rounded-[10px]" />
        crikea
      </h1>
      <p className="mt-2 opacity-70">{t("app.tagline")}</p>

      {hasPasskey && mode === "signIn" && (
        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button" onClick={() => void withPasskey()} disabled={busy}
            className="rounded bg-[var(--color-accent)] px-5 py-3 font-semibold text-white disabled:opacity-50"
          >
            {t("auth.passkey")}
          </button>
          <div className="flex items-center gap-3 text-sm opacity-50">
            <span className="h-px flex-1 bg-current" />
            {t("auth.passkeyHint")}
            <span className="h-px flex-1 bg-current" />
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest opacity-60">
            {t("auth.email")}
          </span>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required autoComplete="username" autoFocus
            className="rounded border border-current/25 bg-transparent px-3 py-2.5"
          />
        </label>

        {mode === "signIn" && (
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-widest opacity-60">
              {t("auth.password")}
            </span>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required autoComplete="current-password"
              className="rounded border border-current/25 bg-transparent px-3 py-2.5"
            />
          </label>
        )}

        {error && <p role="alert" className="text-[var(--color-bad)]">{error}</p>}
        {notice && <p className="text-[var(--color-accent)]">{notice}</p>}

        <button
          type="submit" disabled={busy}
          className="mt-2 rounded bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white disabled:opacity-50"
        >
          {busy
            ? t(mode === "signIn" ? "auth.signingIn" : "reset.sending")
            : t(mode === "signIn" ? "auth.signIn" : "reset.sendLink")}
        </button>

        <button
          type="button"
          onClick={() => {
            setMode(mode === "signIn" ? "forgot" : "signIn");
            setError(null);
            setNotice(null);
          }}
          className="text-sm underline underline-offset-4 opacity-70 hover:opacity-100"
        >
          {t(mode === "signIn" ? "auth.forgot" : "auth.backToSignIn")}
        </button>
      </form>
    </main>
  );
}
